import type { TableHTMLAttributes } from 'react';
import { cn } from '../lib/utils';
import { Table } from './table';
import { TableScrollRegion } from './table-scroll-region';

export type V2TableProps = TableHTMLAttributes<HTMLTableElement> & {
  /**
   * Give the scroll wrapper a bounded height so a `position: sticky` header
   * inside the table actually sticks.
   *
   * Needed because the wrapper sets `overflow-x: auto`, and CSS computes the
   * other axis from `visible` to `auto` when one axis is not `visible`. That
   * makes the wrapper the nearest scrollport for anything inside it — but with
   * no height limit it never scrolls vertically, so `sticky top-0` has nothing
   * to stick within and silently does nothing. Applying a sticky class to a
   * table in this wrapper without also bounding the height looks correct in
   * review and has no effect in the browser.
   *
   * Opt-in rather than default: bounding the height changes the page rhythm of
   * every table that uses this component, and most of them are short enough
   * that a frozen header would cost more room than it returns.
   */
  stickyHeaderViewport?: boolean;
};

export function V2Table({ className, stickyHeaderViewport, ...props }: V2TableProps) {
  return (
    <TableScrollRegion
      className={cn(
        'min-w-0 max-w-full overflow-x-auto rounded-card border border-border bg-surface-sheet',
        // `overflow-auto` (both axes) plus a height cap is what turns this into
        // a real vertical scrollport using the shared spacing scale.
        stickyHeaderViewport && 'max-h-96 overflow-auto overscroll-contain',
      )}
    >
      <Table
        {...props}
        data-v2-table="true"
        className={cn(
          'statura-data-table w-full min-w-full border-collapse font-body text-body tabular-nums',
          stickyHeaderViewport && '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
          className,
        )}
      />
    </TableScrollRegion>
  );
}
